(function () {
  'use strict';

  if (customElements.get('testimonials-slider')) return;

  customElements.define(
    'testimonials-slider',
    class extends HTMLElement {
      constructor() {
        super();
        this.flkty = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this.onResizeCallback = () => this.onResize();
      }

      connectedCallback() {
        this.container = this.closest('[data-section-type="testimonials"]');
        this.slides = this.querySelectorAll('[data-item]');
        this.externalArrows = this.classList.contains('testimonials__slider--external-arrows');
        this.carousel = this.closest('.testimonials__carousel');
        this.activeGroupCells = null;
        this.checkSlideCount();
        this.listen();
      }

      getGroupCells() {
        if (!this.externalArrows) return true;

        const width = window.innerWidth;

        if (width <= 767) {
          return parseInt(this.dataset.groupCellsMobile, 10) || 1;
        }

        if (width <= 989) {
          return parseInt(this.dataset.groupCellsTablet, 10) || 2;
        }

        return parseInt(this.dataset.groupCells, 10) || 4;
      }

      getGutter() {
        const styles = getComputedStyle(this);
        const gutter = parseFloat(styles.getPropertyValue('--grid-gutter'));

        if (!Number.isNaN(gutter) && gutter > 0) {
          return gutter;
        }

        return 20;
      }

      sizeGroupedCells() {
        if (!this.externalArrows || !this.flkty) return;

        const count = this.getGroupCells();
        const viewport = this.querySelector('.flickity-viewport');

        if (!viewport || count < 1) return;

        const sliderWidth = viewport.getBoundingClientRect().width;
        const gutter = this.getGutter();
        const cellWidth = (sliderWidth - gutter * (count - 1)) / count;

        this.flkty.cells.forEach((cell) => {
          cell.element.style.width = `${cellWidth}px`;
          cell.element.style.marginRight = `${gutter}px`;
        });

        this.flkty.reloadCells();
        this.flkty.resize();
        this.updateExternalArrows();
      }

      onResize() {
        const nextGroupCells = this.getGroupCells();

        if (this.flkty && this.activeGroupCells !== nextGroupCells) {
          this.destroySlider();
          this.activeGroupCells = null;
        }

        this.checkSlideCount();

        if (this.flkty !== null) {
          this.flkty.resize();

          if (this.externalArrows) {
            this.sizeGroupedCells();
          }

          if (this.flkty.size.width >= this.flkty.slideableWidth) {
            this.destroySlider();
          } else {
            this.updateExternalArrows();
          }
        }
      }

      checkSlideCount() {
        const slideCount = this.slides.length;
        const isTwoUp = slideCount === 2 && window.innerWidth >= theme.sizes.small;
        const isSingle = slideCount === 1;

        if (isTwoUp || isSingle) {
          this.destroySlider();
          return;
        }

        if (this.flkty) {
          if (!this.flkty.isActive) {
            this.initSlider();
          } else if (this.flkty.slideableWidth > this.flkty.size.width) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
          return;
        }

        this.initSlider();
        this.onResize();
      }

      initSlider() {
        if (this.flkty) {
          this.destroySlider();
        }

        const groupCells = this.getGroupCells();
        this.activeGroupCells = groupCells;

        this.flkty = new window.theme.Flickity(this, {
          cellSelector: '[data-item]',
          prevNextButtons: !this.externalArrows,
          pageDots: true,
          groupCells: groupCells,
          cellAlign: this.dataset.sliderAlignment || 'left',
          contain: true,
          adaptiveHeight: false,
          imagesLoaded: true,
          on: {
            ready: () => {
              this.slidesTabIndex();
              requestAnimationFrame(() => {
                if (!this.externalArrows) {
                  this.querySelectorAll('.flickity-button').forEach((button) => {
                    window.theme.wrap(button, 'flickity-button-holder');
                  });
                }

                this.bindExternalArrows();

                if (this.flkty) {
                  if (this.externalArrows) {
                    this.sizeGroupedCells();
                  } else {
                    this.flkty.resize();
                  }
                }
              });
            },
            change: () => {
              this.slidesTabIndex();
              this.updateExternalArrows();
            },
          },
        });

        if (window.theme.isMobile() && !this.externalArrows) {
          this.container.classList.remove('has-arrows');
        } else {
          this.container.classList.add('has-arrows');
        }

        this.createResizeClass();
        this.bindExternalArrows();
        this.updateExternalArrows();
      }

      bindExternalArrows() {
        if (!this.externalArrows || !this.carousel || !this.flkty) return;

        this.prevBtn = this.carousel.querySelector('[data-testimonials-prev]');
        this.nextBtn = this.carousel.querySelector('[data-testimonials-next]');

        if (this.prevBtn && !this.prevBtn.dataset.bound) {
          this.prevBtn.dataset.bound = 'true';
          this.prevBtn.addEventListener('click', () => {
            if (this.flkty) this.flkty.previous();
          });
        }

        if (this.nextBtn && !this.nextBtn.dataset.bound) {
          this.nextBtn.dataset.bound = 'true';
          this.nextBtn.addEventListener('click', () => {
            if (this.flkty) this.flkty.next();
          });
        }
      }

      updateExternalArrows() {
        if (!this.externalArrows || !this.flkty) return;

        const lastIndex = Math.max(0, this.flkty.slides.length - 1);
        const atStart = this.flkty.selectedIndex <= 0;
        const atEnd = this.flkty.selectedIndex >= lastIndex;

        if (this.prevBtn) this.prevBtn.disabled = atStart;
        if (this.nextBtn) this.nextBtn.disabled = atEnd;
      }

      createResizeClass() {
        if (typeof window.theme.Flickity.prototype._createResizeClass === 'function') return;

        window.theme.Flickity.prototype._createResizeClass = function () {
          this.element.classList.add('carousel--resize');
        };

        window.theme.Flickity.createMethods.push('_createResizeClass');

        const nativeResize = window.theme.Flickity.prototype.resize;

        window.theme.Flickity.prototype.resize = function () {
          this.element.classList.remove('carousel--resize');
          nativeResize.call(this);
          this.element.classList.add('carousel--resize');
        };
      }

      slidesTabIndex() {
        window.theme.Flickity.data(this).cells.forEach((cell) => {
          let tabIndex = '-1';

          if (cell.element.classList.contains('is-selected')) {
            tabIndex = '0';
          }

          cell.element.querySelectorAll('a, button').forEach((el) => {
            el.setAttribute('tabindex', tabIndex);
          });
        });
      }

      destroySlider() {
        if (!this.classList.contains('flickity-enabled')) return;

        if (this.flkty !== null) {
          this.querySelectorAll('.flickity-button')?.forEach((button) => {
            const parent = button.parentNode;

            if (parent && parent.classList.contains('flickity-button-holder')) {
              parent.parentNode.insertBefore(button, parent);
              parent.parentNode.removeChild(parent);
            }
          });

          this.flkty.destroy();
          this.flkty = null;
        }

        this.container.classList.remove('has-arrows');
        this.activeGroupCells = null;

        if (this.externalArrows && this.carousel) {
          const prevBtn = this.carousel.querySelector('[data-testimonials-prev]');
          const nextBtn = this.carousel.querySelector('[data-testimonials-next]');
          if (prevBtn) prevBtn.disabled = true;
          if (nextBtn) nextBtn.disabled = true;
        }
      }

      listen() {
        document.addEventListener('theme:resize:width', this.onResizeCallback);
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize:width', this.onResizeCallback);
      }
    }
  );
})();
